/** AI proxy client (§5.10, §3.5). Every call: typed errors, cost recorded locally,
 *  month status surfaced, nothing blocks a manual path. Model output is untrusted
 *  data — every response is schema-validated before it touches app state. */
import { z } from 'zod';
import { createRecord, getSettings } from '../core/db';
import { nowIso } from '../core/ids';
import { signal } from '@preact/signals';

export type AiTask =
  | 'photo_inventory' | 'aerial_inventory' | 'scope_parse' | 'bid_map' | 'proposal_draft'
  | 'sanity_check' | 'field_notes' | 'email_draft';

export interface AiMonth { spentUsd: number; capUsd: number; status: 'ok' | 'warn' | 'blocked' }
export const aiMonthSig = signal<AiMonth | null>(null);

export class AiError extends Error {
  constructor(public code: string, message: string, public retryable = false) { super(message); }
}

const RespSchema = z.object({
  ok: z.literal(true),
  data: z.unknown(),
  usage: z.object({ inputTokens: z.number().optional(), outputTokens: z.number().optional(), costUsd: z.number() }),
  month: z.object({ spentUsd: z.number(), capUsd: z.number(), status: z.enum(['ok', 'warn', 'blocked']) }),
  cached: z.boolean(),
});
const ErrSchema = z.object({
  ok: z.literal(false),
  error: z.object({ code: z.string(), message: z.string(), retryable: z.boolean().optional() }),
  month: z.object({ spentUsd: z.number(), capUsd: z.number(), status: z.enum(['ok', 'warn', 'blocked']) }).optional(),
});

/**
 * The proxy address is typed by hand into Settings on every device, so a typo is
 * the likeliest configuration mistake there is. Audit 2026-07-25: entering
 * "not-a-url" made fetch() resolve it against the app's own origin, the host
 * returned its HTML page, JSON.parse failed, and the user was told "The AI
 * answered in a shape the app refuses to trust" — which sends them looking at
 * the model instead of at the one field they mistyped.
 */
export function proxyUrlProblem(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;                                  // empty is "not configured", a different state
  let u: URL;
  try { u = new URL(v); } catch { return 'That is not a full web address. It should start with https:// — the one from your handoff notes ends in .workers.dev'; }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return 'The address has to start with https://';
  if (!u.hostname.includes('.')) return 'That address is missing a domain name. It should look like https://something.workers.dev';
  return null;
}

export async function aiConfigured(): Promise<boolean> {
  const s = await getSettings();
  return !!(s.proxyUrl && s.proxyPassphrase && !proxyUrlProblem(s.proxyUrl));
}

/** Plain-language explanations for every error code — shown verbatim in banners. */
export function explainAiError(e: unknown): string {
  if (e instanceof AiError) {
    switch (e.code) {
      case 'not_configured': return 'The AI assistant is not connected. Add the proxy address and device passphrase in Settings — everything manual keeps working without it.';
      case 'bad_proxy_url': return `${e.message} Everything manual keeps working meanwhile.`;
      case 'unauthorized': return 'The proxy rejected this device’s passphrase. Re-enter it in Settings.';
      case 'month_cap_reached': return 'The monthly AI budget is used up. AI features are off until the 1st; every manual path still works.';
      case 'day_cap_reached': return 'Today’s AI budget is used up. It resets at midnight; manual paths keep working.';
      case 'rate_limited': return 'The AI service asked us to slow down. Wait a minute and try again.';
      case 'upstream_timeout': return 'The AI service did not answer in time. Try again, or carry on by hand.';
      case 'malformed_model_output': return 'The AI answered in a shape the app refuses to trust. Nothing was changed. Try again, or carry on by hand.';
      default: return `${e.message} Nothing was changed; the manual path still works.`;
    }
  }
  return 'The AI call failed unexpectedly. Nothing was changed; the manual path still works.';
}

export async function callTask<T>(task: AiTask, input: unknown, dataSchema: z.ZodType<T>): Promise<{ data: T; costUsd: number; cached: boolean }> {
  const s = await getSettings();
  if (!s.proxyUrl || !s.proxyPassphrase) throw new AiError('not_configured', 'AI is not configured.');
  const urlProblem = proxyUrlProblem(s.proxyUrl);
  if (urlProblem) throw new AiError('bad_proxy_url', urlProblem);
  let res: Response;
  try {
    res = await fetch(`${s.proxyUrl.replace(/\/$/, '')}/v1/task/${task}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${s.proxyPassphrase}` },
      body: JSON.stringify({ input }),
      signal: AbortSignal.timeout(90000),
    });
  } catch (e) {
    if ((e as Error).name === 'TimeoutError') throw new AiError('upstream_timeout', 'The AI service timed out.', true);
    throw new AiError('network', 'The AI proxy could not be reached.', true);
  }
  // An HTML body means we reached something that is not the proxy — a wrong
  // address, a captive portal, or a host serving its own 404 page. That is a
  // configuration fault and must not be reported as the model misbehaving.
  const ctype = res.headers.get('content-type') ?? '';
  if (!ctype.includes('json')) {
    throw new AiError('bad_proxy_url',
      `The address in Settings answered, but not like the AI proxy does (it sent ${ctype.split(';')[0] || 'no content type'}). Check the proxy address.`);
  }
  let body: unknown;
  try { body = await res.json(); } catch { throw new AiError('bad_proxy_url', 'The address in Settings answered with something that is not the AI proxy. Check the proxy address.'); }

  const err = ErrSchema.safeParse(body);
  if (err.success) {
    if (err.data.month) aiMonthSig.value = err.data.month;
    throw new AiError(err.data.error.code, err.data.error.message, err.data.error.retryable ?? false);
  }
  const ok = RespSchema.safeParse(body);
  if (!ok.success || !res.ok) throw new AiError('malformed_model_output', 'The proxy answer did not match the expected shape.');
  aiMonthSig.value = ok.data.month;

  const parsed = dataSchema.safeParse(ok.data.data);
  if (!parsed.success) throw new AiError('malformed_model_output', 'The AI’s answer failed validation and was discarded.');

  await createRecord('aiCalls', {
    task, at: nowIso(),
    inputTokens: ok.data.usage.inputTokens, outputTokens: ok.data.usage.outputTokens,
    costUsd: ok.data.usage.costUsd, cacheHit: ok.data.cached, ok: true,
  }, { snapshot: false });

  return { data: parsed.data, costUsd: ok.data.usage.costUsd, cached: ok.data.cached };
}

export async function refreshUsage(): Promise<void> {
  const s = await getSettings();
  if (!s.proxyUrl || !s.proxyPassphrase) { aiMonthSig.value = null; return; }
  try {
    const res = await fetch(`${s.proxyUrl.replace(/\/$/, '')}/v1/usage`, {
      headers: { Authorization: `Bearer ${s.proxyPassphrase}` }, signal: AbortSignal.timeout(8000),
    });
    const body = z.object({ monthUsd: z.number(), monthCapUsd: z.number(), status: z.enum(['ok', 'warn', 'blocked']) }).safeParse(await res.json());
    if (body.success) aiMonthSig.value = { spentUsd: body.data.monthUsd, capUsd: body.data.monthCapUsd, status: body.data.status };
  } catch { /* usage chip simply doesn't render; features fail loudly on their own */ }
}
